import React, { useState, useEffect } from 'react';
import Sidebar from '../components/layout/Sidebar';
import SidebarToggle from '../components/layout/SidebarToggle';
import { ToastContainer } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';

// Pages
import MapPage from '../pages/MapPage';
import MissionsPage from '../pages/MissionsPage';
import DeliveriesPage from '../pages/DeliveriesPage';
import DronesPage from '../pages/DronesPage';
import LogsPage from '../pages/LogsPage';
import ProfilePage from '../pages/ProfilePage';
import AdminPage from '../pages/AdminPage';
import SettingsPage from '../pages/SettingsPage';

import { SECTIONS } from '../config/constants';

/**
 * Desktop Layout - Sidebar + Main Content
 */
function DesktopLayout({
  activeSection,
  setActiveSection,
  user,
  logout,
  telemetry,
  dronesTelemetry,
  activeMissions,
  drones,
  reorderDrones,
  loadDrones,
  selectedDroneId,
  setSelectedDroneId,
  toasts,
  dismissToast,
  confirmDialog,
  handleConfirm,
}) {
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const isMapSection = activeSection === SECTIONS.MAP;

  // Keep the nav sidebar open on the Map, auto-collapse it on other pages so they
  // get more room. Runs only on section change, so manual toggling still works within a page.
  useEffect(() => {
    setSidebarVisible(activeSection === SECTIONS.MAP);
  }, [activeSection]);
  const handleMapDroneSelectLocal = (drone) => {
    if (!drone) {
      setSelectedDroneId(null);
      return;
    }
    const id = typeof drone === 'string' ? drone : (drone.id || drone.droneId || null);
    setSelectedDroneId(id);
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const renderPage = () => {
    switch (activeSection) {
      case SECTIONS.MAP:
        return (
          <MapPage
            telemetry={telemetry}
            dronesTelemetry={dronesTelemetry}
            activeMissions={activeMissions}
            drones={drones}
            sidebarVisible={sidebarVisible}
            onDroneSelect={handleMapDroneSelectLocal}
            selectedDroneId={selectedDroneId}
            isMobileLayout={false}
            topOffset={0}
            currentUserRole={user?.role || 'operator'}
          />
        );

      case SECTIONS.MISSIONS:
        return (
          <MissionsPage
            telemetry={telemetry}
            dronesTelemetry={dronesTelemetry}
            activeMissions={activeMissions}
            drones={drones}
            selectedDroneId={selectedDroneId}
            onDroneSelect={setSelectedDroneId}
            sidebarVisible={sidebarVisible}
            isMobileLayout={false}
            topOffset={0}
          />
        );

      case SECTIONS.DELIVERIES:
        return (
          <DeliveriesPage
            drones={drones}
            dronesTelemetry={dronesTelemetry}
            selectedDroneId={selectedDroneId}
            onDroneSelect={setSelectedDroneId}
            onNavigate={setActiveSection}
          />
        );

      case SECTIONS.DRONES:
        return (
          <DronesPage
            telemetry={telemetry}
            dronesTelemetry={dronesTelemetry}
            drones={drones}
            reorderDrones={reorderDrones}
            loadDrones={loadDrones}
            selectedDroneId={selectedDroneId}
            onDroneSelect={setSelectedDroneId}
            isMobileLayout={false}
          />
        );

      case SECTIONS.LOGS:
        return <LogsPage telemetry={telemetry} dronesTelemetry={dronesTelemetry} selectedDroneId={selectedDroneId} />;

      case SECTIONS.PROFILE:
        return <ProfilePage />;

      case SECTIONS.ADMIN:
        if (user?.role !== 'admin') {
          return <MapPage telemetry={telemetry} sidebarVisible={sidebarVisible} currentUserRole={user?.role || 'operator'} />;
        }
        return <AdminPage dronesTelemetry={dronesTelemetry} />;

      case SECTIONS.SETTINGS:
        return <SettingsPage />;

      default:
        return <MapPage telemetry={telemetry} sidebarVisible={sidebarVisible} currentUserRole={user?.role || 'operator'} />;
    }
  };

  return (
    <div className={`app ${sidebarVisible ? 'sidebar-expanded' : 'sidebar-collapsed'} desktop-shell`}>
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        show={confirmDialog.show}
        message={confirmDialog.message}
        onConfirm={() => handleConfirm(true)}
        onCancel={() => handleConfirm(false)}
      />

      {/* Left Sidebar Navigation — on the map it floats above so toggling never resizes/reflows the map */}
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        ...(isMapSection
          ? { position: 'absolute', left: 0, top: 0, height: '100vh', zIndex: 1200 }
          : { position: 'relative' }),
      }}>
        <Sidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          sidebarVisible={sidebarVisible}
          currentUser={user}
          onLogout={logout}
        />

        <SidebarToggle
          isExpanded={sidebarVisible}
          onToggle={toggleSidebar}
          position="left"
        />
      </div>

      {/* Main Content Area */}
      <div className="main-content">
        {/* Page Content */}
        <div style={{
          display: activeSection === SECTIONS.MAP ? 'block' : 'none',
          height: '100vh',
          overflow: 'hidden'
        }}>
          <MapPage
            telemetry={telemetry}
            dronesTelemetry={dronesTelemetry}
            activeMissions={activeMissions}
            drones={drones}
            sidebarVisible={sidebarVisible}
            onDroneSelect={handleMapDroneSelectLocal}
            selectedDroneId={selectedDroneId}
            isMobileLayout={false}
            topOffset={0}
            currentUserRole={user?.role || 'operator'}
            isActive={activeSection === SECTIONS.MAP}
          />
        </div>

        <div style={{
          display: activeSection === SECTIONS.MISSIONS ? 'block' : 'none',
          height: '100vh'
        }}>
          {activeSection === SECTIONS.MISSIONS && renderPage()}
        </div>

        <div style={{
          display: activeSection === SECTIONS.DELIVERIES ? 'block' : 'none',
          height: '100vh'
        }}>
          {activeSection === SECTIONS.DELIVERIES && renderPage()}
        </div>

        <div style={{
          display: activeSection === SECTIONS.DRONES ? 'flex' : 'none',
          height: '100vh'
        }}>
          {activeSection === SECTIONS.DRONES && renderPage()}
        </div>

        <div style={{
          display: activeSection === SECTIONS.LOGS ? 'block' : 'none',
          height: '100vh'
        }}>
          {activeSection === SECTIONS.LOGS && renderPage()}
        </div>

        <div style={{
          display: activeSection === SECTIONS.PROFILE ? 'block' : 'none',
          height: '100vh'
        }}>
          {activeSection === SECTIONS.PROFILE && renderPage()}
        </div>

        <div style={{
          display: activeSection === SECTIONS.ADMIN ? 'block' : 'none',
          height: '100vh'
        }}>
          {activeSection === SECTIONS.ADMIN && renderPage()}
        </div>

        <div style={{
          display: activeSection === SECTIONS.SETTINGS ? 'block' : 'none',
          height: '100vh'
        }}>
          {activeSection === SECTIONS.SETTINGS && renderPage()}
        </div>
      </div>
    </div>
  );
}

export default DesktopLayout;
